import { AlertDialog as AlertDialogPrimitive } from '@base-ui/react/alert-dialog';
import type { ComponentProps, HTMLAttributes, ReactElement } from 'react';
import { Button, type ButtonProps } from '../primitives/button.tsx';
import { cn } from '../util/classes.ts';
import { ContainerContext } from './container.tsx';

export type AlertDialogProps = AlertDialogPrimitive.Root.Props;

export function AlertDialog({ ...props }: AlertDialogProps): ReactElement {
    return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

export type AlertDialogTriggerProps = AlertDialogPrimitive.Trigger.Props;

export function AlertDialogTrigger({ ...props }: AlertDialogTriggerProps): ReactElement {
    return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

export type AlertDialogPortalProps = AlertDialogPrimitive.Portal.Props;

export function AlertDialogPortal({ ...props }: AlertDialogPortalProps): ReactElement {
    return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

export type AlertDialogOverlayProps = AlertDialogPrimitive.Backdrop.Props;

export function AlertDialogOverlay({ className, ...props }: AlertDialogOverlayProps): ReactElement {
    return (
        <AlertDialogPrimitive.Backdrop
            data-slot="alert-dialog-overlay"
            className={cn('motion-scrim fixed inset-0 z-50 bg-scrim/40', className)}
            {...props}
        />
    );
}

export type AlertDialogContentProps = AlertDialogPrimitive.Popup.Props;

export function AlertDialogContent({ className, ...props }: AlertDialogContentProps): ReactElement {
    const level = 1;
    return (
        <ContainerContext level={level}>
            <AlertDialogPortal>
                <AlertDialogOverlay />
                <AlertDialogPrimitive.Popup
                    data-slot="alert-dialog-content"
                    className={cn(
                        'group/alert-dialog-content',
                        'fixed z-50 grid rounded-relaxed bg-container text-content outline-none',
                        'inset-x-container-cozy bottom-container-cozy border border-line-weak shadow-2xl',
                        'sm:inset-0 sm:m-auto sm:h-fit sm:w-full sm:max-w-sm sm:border-none sm:shadow-xl',
                        'motion-dialog',
                        className,
                    )}
                    {...props}
                    // we're doing this manually because adding a <Container />
                    // messes up the animation for some reason
                    data-container={level}
                    data-level={level}
                />
            </AlertDialogPortal>
        </ContainerContext>
    );
}

export type AlertDialogHeaderProps = ComponentProps<'div'>;

export function AlertDialogHeader({ className, ...props }: AlertDialogHeaderProps): ReactElement {
    return (
        <div
            data-slot="alert-dialog-header"
            className={cn(
                'grid grid-rows-[auto_1fr] place-items-start gap-container-cozy px-5 pt-5 pb-0 sm:px-6',
                className,
            )}
            {...props}
        />
    );
}

export type AlertDialogFooterProps = HTMLAttributes<HTMLDivElement>;

export function AlertDialogFooter({ className, ...props }: AlertDialogFooterProps): ReactElement {
    return (
        <div
            data-slot="alert-dialog-footer"
            className={cn(
                'flex flex-row justify-end gap-container-cozy px-5 pt-container-cozy pb-5 sm:px-6',
                className,
            )}
            {...props}
        />
    );
}

export type AlertDialogTitleProps = AlertDialogPrimitive.Title.Props;

export function AlertDialogTitle({ className, ...props }: AlertDialogTitleProps): ReactElement {
    return (
        <AlertDialogPrimitive.Title
            data-slot="alert-dialog-title"
            className={cn('text-ui-lg font-medium', className)}
            {...props}
        />
    );
}

export type AlertDialogDescriptionProps = AlertDialogPrimitive.Description.Props;

export function AlertDialogDescription({ className, ...props }: AlertDialogDescriptionProps): ReactElement {
    return (
        <AlertDialogPrimitive.Description
            data-slot="alert-dialog-description"
            className={cn(
                'text-ui-base text-pretty text-content-weak',
                '*:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-content',
                className,
            )}
            {...props}
        />
    );
}

export type AlertDialogActionProps = ButtonProps;

export function AlertDialogAction({ ...props }: AlertDialogActionProps): ReactElement {
    return <Button data-slot="alert-dialog-action" {...props} />;
}

export type AlertDialogCancelProps = AlertDialogPrimitive.Close.Props;

export function AlertDialogCancel({ ...props }: AlertDialogCancelProps): ReactElement {
    return <AlertDialogPrimitive.Close data-slot="alert-dialog-cancel" render={<Button />} {...props} />;
}
